// Copyright (C) Lembani Sakala - All rights reserved.
// Lembani Sakala.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.

import React, { useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Formik, Field } from 'formik';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { loadVehicleTypesAsync, selectVehicleTypeById } from '../../../app/vehicle-type/vehicle-typeSlice';
import { RootState } from '../../../app/store';
import { FaSave, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaSlidersH, FaSuitcase } from 'react-icons/fa';
import { convertDate } from '../../../utils/formatDateToString';

interface BookingModalProps {
  vt: number;
  show: boolean;
  onHide: () => void;
  onSubmit: (values: BookingFormData) => void;
}

interface BookingFormData {
  id: number | string;
  email: string;
  firstname: string;
  lastname: string;
  pickUpTown: string;
  pickUpArea: string;
  drivingToTown: string;
  drivingToCountry: string;
  dropOffTown: string;
  startDate: Date | any;
  endDate: Date | any;
  vehicleType: string | number;
  driveMode: string;
  numOfAdults: number;
  numOfChildren: number;
  selectedRate: string;
  bookingNumber: string;
  phone: string;
  status: string;
  totalDriverAllowance: number;
  totalBookingAmount: number;
  finalTotalAmount: number;
  discount: number;
  bookingRateAmount: number;
  driverRateAmount: number;
  pumpPrice: number;
  distance: number;
  fuelCost: number;
  tollFees: number;
  driverFood: number;
  busFare: number;
  driverLodging: number;
  accessories: string[];
  accessoriesTotal: number;
}

const ACCESSORIES = ['Jerry Cans', 'Satellite Phone', 'GPS', 'Cooler Box', 'Ground tent / Mattresses / Beddings', 'Camping Equipments'];

export const EditBookingModalRental: React.FC<BookingModalProps> = ({ vt, show, onHide, onSubmit }) => {
  const dispatch = useAppDispatch();
  const { quote } = useAppSelector((state: RootState) => state.booking);
  let vehicleType = useAppSelector((state: RootState) =>
    selectVehicleTypeById(state, quote?.booking?.vehicleType?.id | vt)
  );

  const formattedStartDate = convertDate(quote?.booking?.startDate);
  const formattedEndDate = convertDate(quote?.booking?.endDate);

  useEffect(() => {
    dispatch(loadVehicleTypesAsync());
  }, [dispatch]);

  return (
    <Modal show={show} onHide={onHide} dialogClassName='booking-modal' size='lg'>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Edit Your Quote</Modal.Title>
          <div className='booking-subtitle'>#{quote?.booking?.bookingNumber}</div>
          {vehicleType && <span className='vehicle-badge'>{vehicleType.type}</span>}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Formik<BookingFormData>
          initialValues={{
            id: quote?.booking?.id,
            email: quote?.booking?.email,
            firstname: quote?.booking?.firstname,
            lastname: quote?.booking?.lastname,
            pickUpTown: quote?.booking?.pickUpTown,
            pickUpArea: quote?.booking?.pickUpArea,
            drivingToTown: quote?.booking?.drivingToTown,
            drivingToCountry: quote?.booking?.drivingToCountry,
            dropOffTown: quote?.booking?.dropOffTown,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            vehicleType: vehicleType?.id,
            driveMode: quote?.booking?.driveMode,
            numOfAdults: quote?.booking?.numOfAdults,
            numOfChildren: quote?.booking?.numOfChildren,
            selectedRate: quote?.booking?.selectedRate,
            bookingNumber: quote?.booking?.bookingNumber,
            phone: quote?.booking?.phone,
            status: quote?.booking?.status,
            totalDriverAllowance: quote?.booking?.totalDriverAllowance,
            totalBookingAmount: quote?.booking?.totalBookingAmount,
            finalTotalAmount: quote?.booking?.finalTotalAmount,
            discount: quote?.booking?.discount,
            bookingRateAmount: quote?.booking?.bookingRateAmount,
            driverRateAmount: quote?.booking?.driverRateAmount,
            pumpPrice: quote?.booking?.pumpPrice,
            distance: quote?.booking?.distance,
            fuelCost: quote?.booking?.fuelCost,
            tollFees: quote?.booking?.tollFees,
            driverFood: quote?.booking?.driverFood,
            busFare: quote?.booking?.busFare,
            driverLodging: quote?.booking?.driverLodging,
            accessories: quote?.booking?.accessories || [],
            accessoriesTotal: quote?.booking?.accessoriesTotal || 0,
          }}
          onSubmit={onSubmit}
        >
          {({ values, handleChange, handleSubmit, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>

              {/* Personal Details */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaUser /> Personal Details
                </div>
                <div className='booking-fields-row'>
                  <Form.Group controlId='edit-firstname'>
                    <Form.Label>First Name</Form.Label>
                    <Field type='text' name='firstname' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-lastname'>
                    <Form.Label>Last Name</Form.Label>
                    <Field type='text' name='lastname' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-email'>
                    <Form.Label>Email Address</Form.Label>
                    <Field type='email' name='email' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-phone'>
                    <Form.Label>Phone</Form.Label>
                    <Field type='phone' name='phone' as={Form.Control} required />
                  </Form.Group>
                </div>
              </div>

              {/* Trip Details */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaMapMarkerAlt /> Trip Details
                </div>
                <div className='booking-fields-row'>
                  <Form.Group controlId='edit-pickUpTown'>
                    <Form.Label>Pickup Town</Form.Label>
                    <Field type='text' name='pickUpTown' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-pickUpArea'>
                    <Form.Label>Pickup Area</Form.Label>
                    <Field type='text' name='pickUpArea' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-drivingToTown'>
                    <Form.Label>Driving To Town</Form.Label>
                    <Field type='text' name='drivingToTown' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-drivingToCountry'>
                    <Form.Label>Driving To Country</Form.Label>
                    <Field type='text' name='drivingToCountry' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-dropOffTown'>
                    <Form.Label>Drop-off Town</Form.Label>
                    <Field type='text' name='dropOffTown' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-vehicleType'>
                    <Form.Label>Vehicle Type</Form.Label>
                    <Field as='select' name='vehicleType' disabled className='form-control' required>
                      <option value={vehicleType?.id}>{vehicleType?.type}</option>
                    </Field>
                  </Form.Group>
                </div>
              </div>

              {/* Schedule */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaCalendarAlt /> Schedule
                </div>
                <div className='booking-fields-row'>
                  <Form.Group controlId='edit-startDate'>
                    <Form.Label>Start Date & Pickup Time</Form.Label>
                    <Field type='datetime-local' name='startDate' as={Form.Control} required />
                  </Form.Group>
                  <Form.Group controlId='edit-endDate'>
                    <Form.Label>End Date & Drop-off Time</Form.Label>
                    <Field type='datetime-local' name='endDate' as={Form.Control} required />
                  </Form.Group>
                </div>
              </div>

              {/* Preferences */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaSlidersH /> Preferences
                </div>

                <div className='mb-3'>
                  <Form.Label>Do you need a driver?</Form.Label>
                  <div className='toggle-btn-group'>
                    <button
                      type='button'
                      className={`toggle-btn ${values.driveMode === 'Chauffeur' ? 'active' : ''}`}
                      onClick={() => setFieldValue('driveMode', 'Chauffeur')}
                    >
                      Yes, Chauffeur
                    </button>
                    <button
                      type='button'
                      className={`toggle-btn ${values.driveMode === 'Self-drive' ? 'active' : ''}`}
                      onClick={() => setFieldValue('driveMode', 'Self-drive')}
                    >
                      No, Self-drive
                    </button>
                  </div>
                </div>

                <div className='mb-3'>
                  <Form.Label>Currency</Form.Label>
                  <div className='toggle-btn-group'>
                    <button
                      type='button'
                      className={`toggle-btn ${values.selectedRate === 'USD' ? 'active' : ''}`}
                      onClick={() => setFieldValue('selectedRate', 'USD')}
                    >
                      $ USD
                    </button>
                    <button
                      type='button'
                      className={`toggle-btn ${values.selectedRate === 'ZMW' ? 'active' : ''}`}
                      onClick={() => setFieldValue('selectedRate', 'ZMW')}
                    >
                      K ZMW
                    </button>
                  </div>
                </div>

                <div className='booking-fields-row'>
                  <Form.Group controlId='edit-numOfAdults'>
                    <Form.Label>No. of Adults</Form.Label>
                    <Field type='number' name='numOfAdults' as={Form.Control} required min={1} />
                  </Form.Group>
                  <Form.Group controlId='edit-numOfChildren'>
                    <Form.Label>No. of Children</Form.Label>
                    <Field type='number' name='numOfChildren' as={Form.Control} min={0} />
                  </Form.Group>
                </div>
              </div>

              {/* Accessories */}
              <div className='booking-section'>
                <div className='booking-section-title'>
                  <FaSuitcase /> Optional Accessories
                </div>
                <div className='accessory-chips'>
                  {ACCESSORIES.map((acc) => {
                    const isSelected = values.accessories?.includes(acc);
                    return (
                      <label
                        key={acc}
                        className={`accessory-chip ${isSelected ? 'selected' : ''}`}
                      >
                        <input
                          type='checkbox'
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFieldValue('accessories', [...(values.accessories || []), acc]);
                            } else {
                              setFieldValue('accessories', (values.accessories || []).filter(a => a !== acc));
                            }
                          }}
                        />
                        {acc}
                      </label>
                    );
                  })}
                </div>
                <small className='text-muted d-block mt-2'>Select any extras you need for your trip.</small>
                <div className='mt-2'>
                  <strong>Accessories Total: K{values.accessoriesTotal || 0}</strong>
                </div>
              </div>

              <button className='booking-cta' type='submit'>
                <FaSave className='me-2' /> Amend Quote
              </button>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
